import { logger } from '@adornis/base/logging.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { selectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { defineMailTemplate } from '@adornis/mails/db/mail-template-definition.js';
import { ComponentParamaterType } from '@adornis/whatsapp/server/types.js';
import { getInstitutionByID } from '../api/institution.js';
import { getMailTemplateForInstitution } from '../api/mail-templates.js';
import { bhBaseQlContext } from '../bh-context.js';
import { ColorDefinitions } from '../entities/ColorDefinitions.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../entities/mail-templates.js';
import { SettingColorDefintionProp } from '../entities/settings.js';
import { User } from '../entities/user.js';
import { UserNotFoundError } from '../errors/UserNotFoundError.js';
import { assembleInstitutionMail } from '../mails/assemble-mail.js';
import { institutionMailHint, institutionMailVariables } from '../mails/mail-template-catalog.js';
import { sendInstitutionEmail } from '../mails/sendInstitutionMail.js';
import { getNotificationType, sendWhatsappTemplateMessage } from './_helper.js';

defineMailTemplate(MailTemplateType.OTP, {
  getMailData: arg1 => ({ data: {}, attachments: {}, scope: 'default' }),
  defaultTemplate: {
    subject: 'Login Code',
    content: `<p>Hallo {{title}} {{firstname}} {{lastname}},</p>
<p>Hiermit erhalten Sie ihren Login Code. Der Code ist nur kurze Zeit g&uuml;ltig. Geben Sie diesen nicht an dritte weiter:</p>
<p style="text-align: center;"><strong>{{otp-code}}</strong></p>
<p>Sollten Sie keine Anmeldung selbst vorgenommen haben, ignorieren Sie diese E-Mail und geben sie auf keinen Fall den Code an Dritte.</p>
<p>Viele Gr&uuml;&szlig;e</p>
<p>------------------------------------------------------------------</p>
<p>Hello {{title}} {{firstname}} {{lastname}},</p>
<p>you are receiving you're login token. This token is only valid for a short amount of time and should not be given to any third party:</p>
<p style="text-align: center;"><strong>{{otp-code}}</strong></p>
<p>If you did not register yourself, please ignore this e-mail and do not share this code with third parties.</p>
<p>Best regards</p>`,
    footer: '',
  },
  variables: institutionMailVariables(MailTemplateType.OTP),
  attachments: [],
  displayName: 'OTP',
  editorHint: () => institutionMailHint(MailTemplateType.OTP),
});

export async function notifyOtp(userID: string | null, code: string, institutionID?: string, email?: string) {
  const user = userID ? await loadMongoData(User, { _id: userID }).then(users => users[0]) : null;
  if (userID && !user) throw new UserNotFoundError(userID);
  if (!user && !email) throw new Error('OTP recipient missing');

  // Get institution with color definitions if institutionID is provided
  if (institutionID) {
    const institution = await getInstitutionByID(institutionID)({
      _id: 1,
      name: 1,
      colorDefinitions: selectionSet(() => ColorDefinitions, 4),
      settings: {
        colorDefinitions: selectionSet(() => ColorDefinitions, 4),
        inheritedColorDefinitions: selectionSet(() => SettingColorDefintionProp, 4),
      },
    });

    // Set mail theme
    if (institution) {
      bhBaseQlContext.colorDefinitions =
        institution?.settings?.colorDefinitions ?? institution?.settings?.inheritedColorDefinitions?.value;
    }
  }

  const mailTemplate = await getMailTemplateForInstitution(
    institutionID,
    MailTemplateType.OTP,
  )(InstitutionMailTemplate.allFields);

  const notificationType = user ? await getNotificationType(user, mailTemplate, institutionID) : 'email';
  if (user && user.emailConfirmed === true && notificationType === 'whatsapp') {
    const result = await sendWhatsappTemplateMessage({
      institutionID: institutionID!,
      user: { _id: user._id, whatsappPhone: user.whatsappPhone! },
      mailTemplateType: MailTemplateType.OTP,
      parameters: [code],
      buttonUrlParameters: {
        index: '0',
        parameters: [{ type: ComponentParamaterType.TEXT, text: code }],
      },
    });

    if (result.ok) return;
    logger.warn({ error: result.error, userID, institutionID }, 'otp whatsapp message failed; falling back to email');
  }

  if (notificationType === 'email' || true /* do not allow disabling otp-email */) {
    const { content: mailContent } = (await assembleInstitutionMail({
      template: mailTemplate,
      infos: {
        [MailTemplateVars.TITLE]: user?.title ?? '',
        [MailTemplateVars.FIRST_NAME]: user ? InstitutionMailTemplate.mapFirstNameVar(user) : '',
        [MailTemplateVars.LAST_NAME]: user ? InstitutionMailTemplate.mapLastNameVar(user) : '',
        [MailTemplateVars.OTP]: InstitutionMailTemplate.mapOTPVar(code),
      } as any,
    })) as { content: any };

    if (!mailContent) throw new Error("mail template couldn't be found.");

    await sendInstitutionEmail({
      email: user?.email ?? email!,
      subject: mailTemplate.subject,
      content: mailContent,
      institutionID,
    });
  } else {
    // no notification send because both are deactivated
    logger.warn(
      { notificationType, notification: mailTemplate.emailType },
      'no notification send because neither email nor whatsapp are activated',
    );
  }
}
